<!-- CmmQuestionnaireAttachmentSelection.vue -->
<script lang="ts" setup>
    import { reactive, computed } from 'vue';
    import { DatabaseIdTypes } from '../../../../Enums/ChoiceEnums';
    import { ActionButtonStyle } from '../../../../lib/Types/Types';
    import { DisplayStyle, LayoutOrientation } from '../../../../Enums/UiEnums';
    import { ChoiceApi, MessageSection } from '../../../../LibMain';
    import { EDocumentsSearchModeType } from '../../../../components/Search/SearchEnums';
    import RadioButtonInput from '../../../../components/RadioButtonInput.vue';
    import SearchEDocuments from '../../../../components/Search/SearchEDocuments.vue';
    import SearchPatientOrderResults from '../../../../components/Search/SearchPatientOrderResults.vue';
    import SearchSoapNotes from '../../../../components/Search/SearchSoapNotes.vue';
    import ChoiceButton from '../../../../components/Buttons/ChoiceButton.vue';
    import { IntDatabaseIdAndNameModel } from '../../../../lib/Models/IntDatabaseIdAndNameModel';
    import ChoiceLinkButton from '../../../../components/Buttons/ChoiceLinkButton.vue';
    import { ISearchDocumentResult } from '../../../../lib/Api/DocumentManager';

    const props = defineProps<{
        patientId: number
    }>();

    const compData = reactive<{
        attachmentType?: DatabaseIdTypes,
        attachmentId?: number,
        attachmentName?: string,
        resetFocus?: boolean
    }>({});

    const emits = defineEmits<{
        attach: [attachmentId: IntDatabaseIdAndNameModel]
    }>();

    function onClickAttachButton()
    {
        if (!compData.attachmentType || !compData.attachmentId)
        {
            alert("The attachment id isn't set");
            return;
        }
        emits("attach", {idType: compData.attachmentType, id: compData.attachmentId, name: compData.attachmentName});
    }

    function updateAttachmentId(attachmentId?: number, name?: string, generatePdf: boolean = false) {
        compData.attachmentId = attachmentId;
        compData.attachmentName = name;
        compData.resetFocus = true;

        if (generatePdf)
        {
            // Load and return
            ChoiceApi.coverMyMeds.startPdfGeneration(compData.attachmentType, compData.attachmentId);
        }
    }

    // ePA allows up to 10MB.  Classic PA is supposed to only allow 5MB.  
    //  CMM said they want to change this in the next couple weeks (Sep 15, 2023). 
    //  We check on the server so just adding an indicator as a courtesy.
    const maxAllowedFileSizeInBytes = computed(() => {
        return 10 * 1024 * 1024;
    });

    function onChangeEDocument(eDocItem?: ISearchDocumentResult)
    {
        if (!eDocItem)
        {
            compData.attachmentId = undefined;
            compData.attachmentName = undefined;
            return;
        }

        updateAttachmentId(eDocItem.imageId, eDocItem.fileName)
    }

</script>

<template>
    <MessageSection v-if="undefined === props.patientId" :displayStyle="DisplayStyle.Danger" :showIcon="true">
        Missing patientId value
    </MessageSection>

    <div class="mb-2">
        <RadioButtonInput v-model="compData.attachmentType" 
                :value="DatabaseIdTypes.image_id" 
                :layout="LayoutOrientation.Horz" 
                @update:model-value="updateAttachmentId()">
            eDocuments
        </RadioButtonInput>
        <RadioButtonInput v-model="compData.attachmentType" 
                :value="DatabaseIdTypes.lab_requisition_id" 
                :layout="LayoutOrientation.Horz" 
                @update:model-value="updateAttachmentId()">
            Order Results
        </RadioButtonInput>
        <RadioButtonInput v-model="compData.attachmentType" 
                :value="DatabaseIdTypes.soap_note_id" 
                :layout="LayoutOrientation.Horz" 
                @update:model-value="updateAttachmentId()">
            Soap Notes
        </RadioButtonInput>
    </div>

    <SearchEDocuments 
        v-if="DatabaseIdTypes.image_id == compData.attachmentType" 
        :idType="DatabaseIdTypes.patient_id" 
        :id="props.patientId"
        :defaultDaysSinceCreated="30"
        :searchMode="EDocumentsSearchModeType.daysSinceCreate"
        :defaultSearchText="'30'"
        :resetFocus="compData.resetFocus"
        :maxAllowedFileSizeInBytes="maxAllowedFileSizeInBytes"
        :allowedFileTypeExtensions="['pdf','tiff','tif','jpg','jpeg','jpe','png']"
        @onChange="onChangeEDocument" />

    <SearchPatientOrderResults 
        v-if="DatabaseIdTypes.lab_requisition_id == compData.attachmentType" 
        :patientId="props.patientId" 
        :defaultDaysSinceCollected="30"
        :defaultSearchText="'30'"
        :resetFocus="compData.resetFocus"
        @onChange="item => updateAttachmentId(item.labRequisitionId, item.collectionDate, true)" />

    <SearchSoapNotes 
        v-if="DatabaseIdTypes.soap_note_id == compData.attachmentType" 
        :idType="DatabaseIdTypes.patient_id" 
        :id="props.patientId" 
        :defaultDaysSinceEncounter="30"
        :defaultSearchText="'30'"
        :resetFocus="compData.resetFocus"
        @onChange="item => updateAttachmentId(item.soapNoteId, item.encounterDate, true)" />

    <div class="mt-1">
        <ChoiceButton :action="ActionButtonStyle.Attach" 
            v-if="compData.attachmentType"
            :displayStyle="DisplayStyle.Success"
            :disabled="!compData.attachmentId"
            @click="onClickAttachButton" />

        <ChoiceLinkButton 
            v-if="compData.attachmentId && compData.attachmentType"
            :href="ChoiceApi.coverMyMeds.getAttachmentUrl(compData.attachmentType, compData.attachmentId)"
            target="_blank"
            :action="ActionButtonStyle.Report" 
            text="Preview" />
    </div>
</template>